import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, ApiError } from '@/shared/api/client';
import { useAuthStore, CompanyMembership } from '@/shared/store/authStore';
import { BRAND, SLOGAN } from '@/shared/branding';
import { sessionOwner, startErpSession } from '@/shared/erpSession';

type LoginResponse = {
  user: { id: string; email: string; full_name: string; roles: string[]; company_id: string };
  company: { id: string; code: string; name: string; functional_currency_code: string; multi_currency_enabled: boolean };
  companies: CompanyMembership[];
};

import { MailIcon, LockIcon, EyeIcon, ArrowRight } from './authIcons';

const SIGN_OUT_NOTICES: Record<string, string> = {
  idle: 'You were signed out due to inactivity. Please log in again.',
  elsewhere: 'You were signed out because this account signed in on another device or browser. Please log in again.',
};

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpStep, setOtpStep] = useState(false);
  // /login?forgot=1 (e.g. "request a new link" from an expired reset page)
  // opens straight onto the forgot-password step.
  // Query read from the in-memory route (the address bar reads "/").
  const query = new URLSearchParams(useLocation().search);
  // Why the previous session ended, from the route the sign-out sent the
  // tab to: ?reason=idle (useIdleLogout) or ?reason=elsewhere (the API
  // client, after the server answered 401 signed_in_elsewhere: one browser
  // session per user, the later sign-in wins).
  const signOutNotice = SIGN_OUT_NOTICES[query.get('reason') || ''] || null;
  const [forgotMode, setForgotMode] = useState(() => query.has('forgot'));
  const [forgotSent, setForgotSent] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const setCompanies = useAuthStore((s) => s.setCompanies);
  const setReady = useAuthStore((s) => s.setReady);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      const data = await api.post<LoginResponse>('/auth/login', { email, password, otp: otpStep ? otp : undefined });
      setSession(data.user, data.company);
      setCompanies(data.companies || []);
      setReady(true);
      // D1: a fresh history session per sign-in
      startErpSession(sessionOwner(data.user.id, data.company.id));
      nav('/');
    } catch (e2) {
      if (e2 instanceof ApiError && (e2.code === 'totp_required' || e2.code === 'totp_invalid' || e2.code === 'totp_replayed')) {
        setOtpStep(true);
        setOtp('');
        setErr(e2.code === 'totp_required' ? null : e2.message);
      } else {
        setErr(e2 instanceof ApiError ? e2.message : 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  }

  async function onForgotSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setForgotSent(true);
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : 'Could not send the reset link');
    } finally {
      setLoading(false);
    }
  }

  const fieldWrap = 'flex items-center gap-2.5 h-11 px-3.5 rounded-xl border border-border bg-surface transition-colors focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/15';
  const fieldInput = 'login-field flex-1 min-w-0 bg-transparent outline-none text-[0.875rem] text-primary-text placeholder:text-muted';
  const labelCls = 'block text-[0.6875rem] font-semibold tracking-[0.14em] text-secondary uppercase';
  const submitBtn = 'login-shimmer relative overflow-hidden w-full h-12 rounded-xl text-white font-semibold text-[0.9375rem] inline-flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 shadow-lg shadow-indigo-500/25 transition-all disabled:opacity-70 disabled:cursor-not-allowed';
  const backBtn = 'w-full text-center text-body text-muted hover:text-primary-text transition';
  const errorBox = 'rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-body text-rose-700';

  return (
    <div className="relative min-h-screen min-h-[100dvh] flex flex-col items-center justify-center px-4 py-6 overflow-hidden bg-[#eef0fb]">
      {/* The original sign-in look (owner 2026-10-16): indigo aurora + drifting
          grid, indigo headline and button. Small accents (icons, slogan, focus)
          use the theme colour as they always did; reduce-motion stops the motion. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="login-blob absolute -top-40 -left-32 h-[34rem] w-[34rem] rounded-full bg-indigo-500/25 blur-[80px]" />
        <div className="login-blob-2 absolute -bottom-48 -right-32 h-[38rem] w-[38rem] rounded-full bg-sky-400/25 blur-[80px]" />
        <div className="login-blob absolute top-1/3 right-1/4 h-[22rem] w-[22rem] rounded-full bg-violet-500/20 blur-[70px]" style={{ animationDelay: '-5s' }} />
        <div className="login-grid absolute inset-0 opacity-[0.5]"
          style={{ backgroundImage: 'linear-gradient(#6366f114 1px, transparent 1px), linear-gradient(90deg, #6366f114 1px, transparent 1px)', backgroundSize: '40px 40px', maskImage: 'radial-gradient(ellipse 80% 60% at 50% 40%, #000 40%, transparent 100%)' }} />
      </div>

      <div className="relative w-full max-w-[420px] -mt-8">
        {/* Tagline headline above the card */}
        <div className="text-center mb-5 login-rise" style={{ animationDelay: '0.05s' }}>
          <h1 className="text-[1.75rem] sm:text-[1.875rem] font-bold tracking-tight text-[#1e1583] leading-[1.15]">
            Run your business.<br />All in one place.
          </h1>
          <p className="text-[0.875rem] text-[#5b5a78] mt-2">Sign in to your workspace</p>
        </div>

        {/* Card */}
        <div className="rounded-2xl border border-indigo-100 bg-white/95 backdrop-blur shadow-xl shadow-indigo-300/40 px-6 sm:px-8 py-6 login-rise" style={{ animationDelay: '0.18s' }}>
          {/* Brand: the Fritech ERP wordmark alone, no icon (owner 2026-11-03) */}
          <div className="flex flex-col items-center justify-center gap-2 mb-5">
            <span className="login-logo">
              <img src="/logo-fritech.png" alt={BRAND} className="h-12 w-auto select-none" draggable={false} />
            </span>
            <div className="text-[0.6875rem] font-semibold tracking-[0.12em] text-primary uppercase">{SLOGAN}</div>
          </div>

          {signOutNotice && (
            // Session expiry is INFORMATION, not a warning — the user did
            // nothing wrong. Uses the fixed `info` semantic pair from
            // Badge.tsx (#E0ECFB / #2D5596) rather than amber, which is
            // reserved for things that actually need attention.
            <div className="mb-3 rounded-card border border-[#C2D8F2] bg-[#E0ECFB] px-3 py-2 text-body text-[#2D5596] flex items-start gap-2">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-[1px] opacity-80" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
              </svg>
              <span>{signOutNotice}</span>
            </div>
          )}
          {forgotMode ? (
          <form onSubmit={onForgotSubmit} className="space-y-4">
            {forgotSent ? (
              <div className="rounded-lg border border-[#C2D8F2] bg-[#E0ECFB] px-3 py-2 text-body text-[#2D5596]">
                If an account exists for <span className="font-semibold">{email}</span>, a password
                reset link is on its way. It stays valid for 60 minutes — check your spam folder too.
              </div>
            ) : (
              <div>
                <label htmlFor="login-forgot-email" className={`${labelCls} mb-1.5`}>Email</label>
                <div className={fieldWrap}>
                  <span className="text-primary shrink-0"><MailIcon /></span>
                  <input id="login-forgot-email" className={fieldInput} autoFocus type="email" value={email}
                    onChange={(e) => setEmail(e.target.value)} autoComplete="username" placeholder="you@company.com" />
                </div>
                <p className="text-[0.75rem] text-muted mt-2">
                  Enter your account email and we will send you a link to reset your password.
                </p>
              </div>
            )}

            {err && <div role="alert" className={errorBox}>{err}</div>}

            {!forgotSent && (
              <button type="submit" disabled={loading} className={submitBtn}>
                {loading ? 'Sending…' : (<>Send reset link <ArrowRight /></>)}
              </button>
            )}
            <button type="button" onClick={() => { setForgotMode(false); setForgotSent(false); setErr(null); }}
              className={backBtn}>
              ← Back to sign in
            </button>
          </form>
          ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            {!otpStep && (
              <>
                <div>
                  <label htmlFor="login-email" className={`${labelCls} mb-1.5`}>Email</label>
                  <div className={fieldWrap}>
                    <span className="text-primary shrink-0"><MailIcon /></span>
                    <input id="login-email" className={fieldInput} autoFocus type="email" value={email}
                      onChange={(e) => setEmail(e.target.value)} autoComplete="username" placeholder="you@company.com" />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label htmlFor="login-password" className={labelCls}>Password</label>
                    <button type="button" onClick={() => { setForgotMode(true); setErr(null); }}
                      className="text-[0.75rem] text-primary hover:underline">
                      Forgot password?
                    </button>
                  </div>
                  <div className={fieldWrap}>
                    <span className="text-muted shrink-0"><LockIcon /></span>
                    <input id="login-password" className={fieldInput} type={showPw ? 'text' : 'password'} value={password}
                      onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" placeholder="••••••••••" />
                    <button type="button" onClick={() => setShowPw((v) => !v)} 
                      className="focus-ring rounded text-muted hover:text-primary-text shrink-0" aria-label={showPw ? 'Hide password' : 'Show password'}>
                      <EyeIcon off={showPw} />
                    </button>
                  </div>
                </div>
              </>
            )}

            {otpStep && (
              <div>
                <label htmlFor="login-otp" className={`${labelCls} mb-1.5`}>Authenticator code</label>
                <div className={fieldWrap}>
                  <span className="text-primary shrink-0"><LockIcon /></span>
                  <input id="login-otp" className={`${fieldInput} tracking-[0.2em] text-center font-numeric`} autoFocus inputMode="text"
                    autoComplete="one-time-code" maxLength={11} value={otp}
                    onChange={(e) => setOtp(e.target.value.toLowerCase().replace(/[^0-9a-z-]/g, ''))} placeholder="123456" />
                </div>
                <p className="text-[0.75rem] text-muted mt-2">Enter the 6-digit code from your authenticator app, or one of your recovery codes (xxxxx-xxxxx).</p>
              </div>
            )}

            {err && <div role="alert" className={errorBox}>{err}</div>}

            <button type="submit" disabled={loading} className={submitBtn}>
              {loading ? 'Signing in…' : (
                <>
                  {otpStep ? 'Verify & sign in' : 'Sign in'} <ArrowRight />
                </>
              )}
            </button>

            {otpStep && (
              <button type="button" onClick={() => { setOtpStep(false); setOtp(''); setErr(null); }}
                className={backBtn}>
                ← Back to sign in
              </button>
            )}
          </form>
          )}

          {/* Footer line */}
          <div className="mt-7 pt-4 text-center text-[0.75rem] text-muted">
            Secure login · {BRAND} cloud · © {new Date().getFullYear()} {BRAND}
          </div>
        </div>

        {/* Sub-caption below the card */}
        <p className="text-center text-[0.8125rem] text-[#6b6a88] mt-4 max-w-[380px] mx-auto leading-relaxed login-rise" style={{ animationDelay: '0.32s' }}>
          AI-powered ERP — sales, inventory, manufacturing, accounting, and analytics, unified.
        </p>
      </div>
    </div>
  );
}
